import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { RulesValidationSchema } from "@/shared";
import { useCreateRuleMutation, useToast } from "@/hooks";
import { CreateRuleDTO } from "@/types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DisplayError } from "@/utils";

type FormCreateRule = {
  handleClose: () => void;
};

export function FormCreateRule({ handleClose }: FormCreateRule) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const initialValues = {
    name: "",
  };

  const { mutateAsync, isLoading } = useCreateRuleMutation();

  const { handleSubmit, ...formik } = useFormik<CreateRuleDTO>({
    initialValues,
    validationSchema: RulesValidationSchema(),
    onSubmit: async (rolesData) => {
      await handleCreateRoleSubmit(rolesData);
    },
  });

  async function handleCreateRoleSubmit(request: CreateRuleDTO) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
          formik.resetForm();
          handleClose();
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-10 px-4 pb-8">
      <div className="flex flex-col gap-2">
        <Label>{t("LABEL.name")}</Label>
        <Input
          isInvalid={formik.touched.name && formik.errors.name}
          isValid={formik.touched.name && !formik.errors.name}
          {...formik.getFieldProps("name")}
          messageError={formik.errors.name}
        />
      </div>
      <Button isLoading={isLoading} className="w-full">
        {t("LABEL.save")}
      </Button>
    </form>
  );
}
